import { Typography } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import {
  ActionWrapper,
  CardContainer,
  Details,
  ItemName,
} from 'features/menu/components/menu-reorder-widget/menu-reorder-card/styled';
import { useAddMappedEntryToCart } from 'features/menu/components/product-wizard/use-add-to-cart';
import { useStoreContext } from 'features/store';

import ActionButton, { ActionButtonSizes } from 'components/action-button';
import { ItemThumbnailWithBadge } from 'components/item-thumbnail-with-badge/item-thumbnail-with-badge';
import { toaster } from 'components/toaster/toaster';
import { ItemAvailabilityStatus } from 'enums/menu';
import useEffectOnce from 'hooks/use-effect-once';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import {
  CustomEventNames,
  EventTypes,
  logAddRecentItemToCartEvent,
  logEvent,
} from 'state/amplitude';
import { t } from 'state/intl/translations';
import { HapticsImpactStyle } from 'utils/haptic';
import { hapticImpact } from 'utils/haptic/haptic';
import { routes } from 'utils/routing';

import { MinimalMappedOrder } from './minimal-menu-reorder-widget-types';

export const MinimalMenuReorderCard = ({
  order,
  toggleDetailsDrawer,
  drawerIsOpen,
}: {
  order: MinimalMappedOrder;
  toggleDetailsDrawer: () => void;
  drawerIsOpen: boolean;
}) => {
  const { replace } = useRouter();
  const { store } = useStoreContext();
  const addMappedEntryToCart = useAddMappedEntryToCart();
  const isMobile = useIsMobileBreakpoint();

  const linkTextKey = drawerIsOpen ? 'hideDetails' : 'viewDetails';
  const linkText = t(linkTextKey);

  const logViewEvent = () => {
    logEvent(CustomEventNames.LAST_ORDER_HOME_VIEW, EventTypes.Other);
  };
  const logReorderEvent = () => logEvent(CustomEventNames.LAST_ORDER_REORDER, EventTypes.Other);
  const logOpenDetailEvent = () => logEvent(CustomEventNames.LAST_ORDER_DETAILS, EventTypes.Other);

  const totalItems = order.orderEntries.reduce((total, entry) => total + (entry.quantity ?? 1), 0);
  const itemNames = order.orderEntries.map(item => item.name).join(' - ');
  const itemHighlighted = order.orderEntries[0];
  const availableEntries = order.orderEntries.filter(
    entry => entry.status === ItemAvailabilityStatus.AVAILABLE
  );

  useEffectOnce(() => {
    logViewEvent();
  });

  const handleReorder = () => {
    if (drawerIsOpen) {
      toggleDetailsDrawer();
    }

    availableEntries.forEach(entry => {
      logAddRecentItemToCartEvent(entry?.name ?? '', store);
      addMappedEntryToCart(entry);
    });

    const [firstEntry] = availableEntries;
    if (firstEntry) {
      const message =
        availableEntries.length === 1
          ? firstEntry.quantity > 1
            ? t('addToCartMultipleSuccess', {
                itemName: firstEntry.name,
                quantity: firstEntry.quantity - 1,
              })
            : t('addToCartSuccess', { itemName: firstEntry.name })
          : t('cartUpdatedRecentReorder', 'Your cart was updated');

      toaster.addToCartToast(message, firstEntry.image);
    }

    replace(routes.cart);
    hapticImpact({ style: HapticsImpactStyle.Light });
    logReorderEvent();
  };

  return (
    <CardContainer>
      {itemHighlighted && (
        <ItemThumbnailWithBadge
          item={{
            name: itemHighlighted.name,
            image: itemHighlighted.image as never,
          }}
          totalItems={totalItems}
        />
      )}
      <Details>
        <ItemName numberOfLines={isMobile ? 2 : 1}>{itemNames}</ItemName>
        <Typography
          variant="link"
          onPress={() => {
            toggleDetailsDrawer();
            logOpenDetailEvent();
          }}
        >
          {linkText}
        </Typography>
      </Details>
      <ActionWrapper>
        <ActionButton
          testID="reorder-button"
          onPress={handleReorder}
          disabled={!availableEntries.length}
          size={ActionButtonSizes.SMALL}
          accessibilityLabel={
            order.orderNumberFormatted
              ? t('reorderWithOrderNumber', { ticketNumber: order.orderNumberFormatted })
              : t('reorder')
          }
        >
          {t('reorder')}
        </ActionButton>
      </ActionWrapper>
    </CardContainer>
  );
};
